import type { Prediction, TripDetailsV2 } from "@b2bportal/air-shopping-api";
import {
  fetchTripDetails,
  flightShopReducers,
  FlightExchangeThunk,
} from "@b2bportal/core-flights";
import { CallState } from "@b2bportal/core-types";
import { TripCategory } from "@overrides/types";
import {
  type CaseReducer,
  type PayloadAction,
  createSlice,
} from "@reduxjs/toolkit";
import {
  SHOP_SUMMARY_ERROR_INSUFFICIENT_POINTS_BALANCE,
  SHOP_SUMMARY_ERROR_ROUTE_NOT_ALLOWED_FOR_FIXED_POINTS_PRICING,
} from "./shopSummaryErrorCodes";
import { fetchFlights } from "./thunks/fetchFlights";
import { fetchMulticityFlights } from "./thunks/fetchMulticityFlights";
import type { FlightsShopState, PricingMode } from "./types";
import { initialShopState } from "./types";

// Re-export types for backwards compatibility
export type { FlightsShopState, PricingMode };

const setFetchFlightsPending: CaseReducer<
  FlightsShopState,
  ReturnType<typeof fetchFlights.pending>
> = (state, action) => {
  if (action.meta?.arg?.searchFixedPointsFares) {
    state.fixedPricingFlightsCallState = CallState.InProcess;
    state.shopSummaryDestinationIneligibleForGridPricing = undefined;
    state.shopSummaryInsufficientPointsBalance = undefined;
  } else {
    state.flexPricingFlightsCallState = CallState.InProcess;
  }
  state.flightsCallState = CallState.InProcess;
  state.flightsLoading = true;
  state.flightsError = false;
};

// The package departure page was left while its shop was loading (the shop
// is aborted). Ends the loading without marking a failure; the next visit
// shops again.
const setFetchFlightsAbandoned: CaseReducer<FlightsShopState> = (state) => {
  state.flightsCallState = CallState.NotCalled;
  state.flexPricingFlightsCallState = CallState.NotCalled;
  state.flightsLoading = false;
};

const setFetchMulticityFlightsSuccess: CaseReducer<
  FlightsShopState,
  ReturnType<typeof fetchMulticityFlights.fulfilled>
> = (state, action) => {
  // Handle both thunk actions (with meta.arg) and regular actions (without meta.arg), needed for packages thunk
  const {
    isFixedPointsPricingEligible = false,
    searchFixedPointsFares = false,
  } = action.meta?.arg || {};

  const { flights } = action.payload;

  // Clear stale one-way/round-trip data to prevent useSelectDepartureFlight
  // from taking the wrong code path when switching from one-way to multi-city
  state.flights = undefined;
  state.fixedPricingFlights = undefined;
  state.flexPricingFlights = undefined;

  // 1. store result for the resolved call
  if (searchFixedPointsFares) {
    state.fixedPricingFlightsError = false;
    state.fixedPricingMulticityFlights = flights;
    state.fixedPricingFlightsCallState = CallState.Success;
    state.routeNotAllowedForFixedPointsPricing = false;
    state.shopSummaryDestinationIneligibleForGridPricing = false;
    state.shopSummaryInsufficientPointsBalance = false;
    state.pricingMode = "fixed";
    // flights displayed in the flight list are rendered from state.flights
    state.multicityFlights = flights;
  } else {
    state.flexPricingMulticityFlights = flights;
    state.flexPricingFlightsError = false;
    state.flexPricingFlightsCallState = CallState.Success;
  }

  // 2. handle non-fixed pricing eligible case (non elite users)
  if (!isFixedPointsPricingEligible) {
    state.multicityFlights = flights;
    state.flightsLoading = false;
    state.flightsCallState = CallState.Success;
    state.flightsError = false;

    return;
  }

  // 3. fallback to flex pricing if fixed pricing call fails - the route is not allowed for points pricing
  if (
    !searchFixedPointsFares &&
    state.fixedPricingFlightsCallState === CallState.Failed
  ) {
    state.multicityFlights = flights;
  }

  //4. finalize loading and global states
  if (bothResolved(state)) {
    state.flightsLoading = false;

    if (state.flexPricingFlightsCallState === CallState.Success) {
      // as long as flex pricing call succeeds, we can render the shop page
      state.flightsCallState = CallState.Success;
      state.flightsError = false;
    }
  }
};

const setFetchMulticityFlightsRejected: CaseReducer<
  FlightsShopState,
  ReturnType<typeof fetchMulticityFlights.rejected>
> = (state, action) => {
  const { searchFixedPointsFares } = action.meta.arg;
  // 1. determine which call failed
  if (searchFixedPointsFares) {
    state.fixedPricingFlightsError = true;
    state.fixedPricingFlightsCallState = CallState.Failed;
    // if fixed pricing call fails, the route is not allowed for points pricing
    state.routeNotAllowedForFixedPointsPricing = true;
    const codes = action.payload?.shopSummaryErrorCodes;
    state.shopSummaryDestinationIneligibleForGridPricing =
      codes?.includes(
        SHOP_SUMMARY_ERROR_ROUTE_NOT_ALLOWED_FOR_FIXED_POINTS_PRICING
      ) ?? false;
    state.shopSummaryInsufficientPointsBalance =
      codes?.includes(SHOP_SUMMARY_ERROR_INSUFFICIENT_POINTS_BALANCE) ?? false;
  } else {
    state.flexPricingFlightsError = true;
    state.flexPricingFlightsCallState = CallState.Failed;
  }

  // 2. fallback to flex pricing if fixed pricing call fails
  if (searchFixedPointsFares && state.flexPricingMulticityFlights) {
    state.pricingMode = "flex";
    state.multicityFlights = state.flexPricingMulticityFlights;
  }

  //3. finalize loading and global states
  if (bothResolved(state)) {
    state.flightsLoading = false;

    // TODO(Danii): need to figure out what kind of UI to show in this scenario
    if (state.flexPricingFlightsCallState === CallState.Failed) {
      state.flightsError = true;
      state.flightsCallState = CallState.Failed;
    }
  }
};

const isResolved = (callState: CallState) =>
  callState === CallState.Success || callState === CallState.Failed;

const bothResolved = (state: FlightsShopState) => {
  const fixedInitiated =
    state.fixedPricingFlightsCallState !== CallState.NotCalled;
  const flexInitiated =
    state.flexPricingFlightsCallState !== CallState.NotCalled;

  // Only wait for calls that were actually initiated
  return (
    (fixedInitiated ? isResolved(state.fixedPricingFlightsCallState) : true) &&
    (flexInitiated ? isResolved(state.flexPricingFlightsCallState) : true)
  );
};

const setFetchFlightsSuccess: CaseReducer<
  FlightsShopState,
  ReturnType<typeof fetchFlights.fulfilled>
> = (state, action) => {
  // Handle both thunk actions (with meta.arg) and regular actions (without meta.arg), needed for packages thunk
  const {
    isFixedPointsPricingEligible = false,
    searchFixedPointsFares = false,
  } = action.meta?.arg || {};

  const { flights, prediction, additionalPointsPurchase, trackingProperties } =
    action.payload;

  // Clear stale multi-city data to prevent useSelectDepartureFlight
  // from taking the wrong code path when switching from multi-city to one-way
  state.multicityFlights = undefined;
  state.fixedPricingMulticityFlights = undefined;
  state.flexPricingMulticityFlights = undefined;

  // 1. store result for the resolved call
  if (searchFixedPointsFares) {
    state.fixedPricingFlightsError = false;
    state.fixedPricingFlights = flights;
    state.fixedPricingFlightsCallState = CallState.Success;
    state.routeNotAllowedForFixedPointsPricing = false;
    state.shopSummaryDestinationIneligibleForGridPricing = false;
    state.shopSummaryInsufficientPointsBalance = false;
    state.pricingMode = "fixed";
    // flights displayed in the flight list are rendered from state.flights
    state.flights = flights;

    state.additionalPointsPurchase = additionalPointsPurchase;
    state.fixedPricingTrackingProperties = trackingProperties;
  } else {
    state.flexPricingFlights = flights;
    state.flexPricingFlightsError = false;
    state.prediction = action.payload.prediction as Prediction;
    state.flexPricingFlightsCallState = CallState.Success;
  }

  // 2. handle non-fixed pricing eligible case (non elite users)
  if (!isFixedPointsPricingEligible) {
    state.flights = flights;
    state.prediction = prediction as Prediction;
    state.flightsLoading = false;
    state.flightsCallState = CallState.Success;
    state.flightsError = false;

    return;
  }

  // 3. fallback to flex pricing if fixed pricing call fails - the route is not allowed for points pricing
  if (
    !searchFixedPointsFares &&
    state.fixedPricingFlightsCallState === CallState.Failed
  ) {
    state.flights = flights;
  }

  //4. finalize loading and global states
  if (bothResolved(state)) {
    state.flightsLoading = false;

    if (state.flexPricingFlightsCallState === CallState.Success) {
      // as long as flex pricing call succeeds, we can render the shop page
      state.flightsCallState = CallState.Success;
      state.flightsError = false;
    }
  }
};

const setFetchFlightsRejected: CaseReducer<
  FlightsShopState,
  ReturnType<typeof fetchFlights.rejected>
> = (state, action) => {
  const { searchFixedPointsFares } = action.meta.arg;
  // 1. determine which call failed
  if (searchFixedPointsFares) {
    state.fixedPricingFlightsError = true;
    state.fixedPricingFlightsCallState = CallState.Failed;
    // if fixed pricing call fails, the route is not allowed for points pricing
    state.routeNotAllowedForFixedPointsPricing = true;
    const codes = action.payload?.shopSummaryErrorCodes;
    state.shopSummaryDestinationIneligibleForGridPricing =
      codes?.includes(
        SHOP_SUMMARY_ERROR_ROUTE_NOT_ALLOWED_FOR_FIXED_POINTS_PRICING
      ) ?? false;
    state.shopSummaryInsufficientPointsBalance =
      codes?.includes(SHOP_SUMMARY_ERROR_INSUFFICIENT_POINTS_BALANCE) ?? false;
  } else {
    state.flexPricingFlightsError = true;
    state.flexPricingFlightsCallState = CallState.Failed;
  }

  // 2. fallback to flex pricing if fixed pricing call fails
  if (searchFixedPointsFares && state.flexPricingFlights) {
    state.pricingMode = "flex";
    state.flights = state.flexPricingFlights;
  }

  //3. finalize loading and global states

  if (bothResolved(state)) {
    state.flightsLoading = false;

    // TODO(Danii): need to figure out what kind of UI to show in this scenario
    if (state.flexPricingFlightsCallState === CallState.Failed) {
      state.flightsError = true;
      state.flightsCallState = CallState.Failed;
    }
  }
};

const setFetchTripDetails = (
  state: FlightsShopState,
  action: PayloadAction<TripDetailsV2>
) => {
  state.tripDetailsById = {
    ...state.tripDetailsById,
    [action.payload.id]: action.payload,
  };

  if (action.payload.additionalPointsPurchase) {
    state.additionalPointsPurchase = action.payload.additionalPointsPurchase;
  }
};

const setPricingMode = (
  state: FlightsShopState,
  action: PayloadAction<{
    pricingMode: PricingMode;
    tripCategory: TripCategory;
  }>
) => {
  state.pricingMode = action.payload.pricingMode;

  // flights displayed in the flight list are rendered from state.flights so we have to switch them out with the correct pricing flights when toggling pricing mode
  if (action.payload.tripCategory === TripCategory.MULTI_CITY) {
    state.multicityFlights =
      action.payload.pricingMode === "fixed"
        ? { ...state.fixedPricingMulticityFlights }
        : { ...state.flexPricingMulticityFlights };
  } else {
    state.flights =
      action.payload.pricingMode === "fixed"
        ? { ...state.fixedPricingFlights }
        : { ...state.flexPricingFlights };
  }
};

export const flightShopSlice = createSlice({
  name: "flightShop",
  initialState: initialShopState,
  reducers: {
    ...flightShopReducers,
    setFetchFlightsPending,
    setFetchFlightsSuccess,
    setFetchFlightsRejected,
    setFetchFlightsAbandoned,
    setPricingMode,
    setFetchMulticityFlightsSuccess,
    setFetchMulticityFlightsRejected,
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchFlights
      .addCase(fetchFlights.pending, setFetchFlightsPending)
      .addCase(fetchFlights.fulfilled, setFetchFlightsSuccess)
      .addCase(fetchFlights.rejected, setFetchFlightsRejected)
      // Handle fetchTripDetails
      .addCase(fetchTripDetails.fulfilled, setFetchTripDetails)
      // Handle fetchMulticityFlights
      .addCase(fetchMulticityFlights.pending, setFetchFlightsPending)
      .addCase(fetchMulticityFlights.fulfilled, setFetchMulticityFlightsSuccess)
      .addCase(fetchMulticityFlights.rejected, setFetchMulticityFlightsRejected)
      // Handle fetchExchangeFlights
      .addCase(FlightExchangeThunk.fetchExchangeFlights.pending, (state) => {
        state.flightsCallState = CallState.InProcess;
        state.flightsLoading = true;
      })
      .addCase(
        FlightExchangeThunk.fetchExchangeFlights.fulfilled,
        (state, action) => {
          state.flightsCallState = CallState.Success;
          state.flightsError = false;
          state.flights = action.payload.flights;
          state.prediction = action.payload.prediction as Prediction;
          state.flightsLoading = false;
        }
      )
      .addCase(FlightExchangeThunk.fetchExchangeFlights.rejected, (state) => {
        state.flightsCallState = CallState.Failed;
        state.flightsError = true;
        state.flightsLoading = false;
      });
  },
});
export const { setChosenOutgoingSlice, setChosenReturnSlice, setSortOption } =
  flightShopSlice.actions;

export const FlightShopActions = flightShopSlice.actions;

export default flightShopSlice.reducer;
